import {Component, ElementRef, Input, ViewChild} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {DatePipe, DecimalPipe} from '@angular/common';
import {Formats} from '../../../../constants/formats';
import {LocaleService} from '../../../../services/locale/locale.service';
import {ProposalSlip} from '../../../../model/proposal-slip';
import {QuestionOptions} from '../../../../constants/question-options';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import {PrivateNotesDialogComponent} from '../../../dialogs/private-notes-dialog/private-notes-dialog.component';
import {Store} from '@ngrx/store';
import {ProposalAppState} from '../../../../store/proposal/reducers/proposal.reducer';
import {PrivateNoteLocation} from '../../../../enum/private-note-location';
import {PrivateNote} from '../../../../model/offer/private-note';
import {selectPrivateNotes} from '../../../../store/proposal/selectors/proposal.selectors';
import {map} from 'rxjs';
import {ProposalService} from '../../../../services/proposal-service/proposal.service';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';

(pdfMake as any).vfs = (pdfFonts as any).pdfMake?.vfs;

@Component({
    selector: 'app-proposal-slip',
    templateUrl: './proposal-slip.component.html',
    styleUrls: ['./proposal-slip.component.scss', '../../../../../styles/create-something.scss'],
    standalone: false
})
export class ProposalSlipComponent {

  backgroundImageIcon: string;
  pdfDocGenerator: pdfMake.TCreatedPdf;
  pdfUrl: any;
  showPdf: boolean = true;

  privateNote$ = this.proposalStore.select(selectPrivateNotes).pipe(
    map(privateNotes => {
      return privateNotes.find(pn => pn.location == PrivateNoteLocation.SLIP);
    })
  );

  @ViewChild('canvasContainer') canvasContainer: ElementRef<HTMLDivElement>;
  @ViewChild('canvasElement') displayCanvas: ElementRef<HTMLCanvasElement>;

  @Input() set proposalSlip(proposalSlip: ProposalSlip) {
    this._proposalSlip = proposalSlip;
    this.setPremiumData();
    this.isLoadingTile = false;
    this.loadBackgroundImage().then(() => this.generatePDF());
  }

  _proposalSlip: ProposalSlip;
  get proposalSlip(): ProposalSlip {
    return this._proposalSlip;
  }

  isLoadingTile = true;
  isDrawing = false;

  premiumSum = 0;
  expectedTotalPremiumSum = 0;
  advancePremiumSum = 0;
  minimumPremiumSum = 0;
  premiumTax = 0;
  premiumTotal = 0;
  premiumPercent = 0;
  totalInsuredAmount = 0;

  constructor(private readonly translate: TranslateService,
              private readonly datePipe: DatePipe,
              private readonly number: DecimalPipe,
              private readonly localeService: LocaleService,
              private readonly proposalStore: Store<ProposalAppState>,
              private readonly dialog: MatDialog,
              private readonly proposalService: ProposalService) {
  }

  formatUuid(uuid: string): string {
    const proposalId = uuid.replace(/-/g, '').substring(0, 9);
    return proposalId.match(/.{1,3}/g).map(group => group.toUpperCase()).join('-');
  }

  isLeadInsurer(isLeading: boolean) {
    return this.translate.instant(isLeading ? 'proposalSlip.isLeadYes' : 'proposalSlip.isLeadNo');
  }
  setPremiumData() {
    this.proposalSlip.filteredOffers.forEach(offer => {
      this.premiumSum += offer.premium;
      this.expectedTotalPremiumSum += offer.expectedTotalPremium;
      this.advancePremiumSum += offer.advancePremium;
      this.minimumPremiumSum += offer.minimumPremium;
    });
    this.totalInsuredAmount = this.proposalSlip.proposalInformation.insuredAmount;
    this.proposalService.getInsuranceTax().subscribe(value => {
      this.premiumTax = this.premiumSum * value;
      this.premiumTotal = this.premiumSum * (1 + value);
    });
    this.premiumPercent = this.premiumSum / this.totalInsuredAmount * 100;
  }

  generatePDF() {
    const docDefinition: any = {
      background: [
        {
          image: this.backgroundImageIcon,
          width: 595,
          height: 842,
        }
      ],
      filename: 'title.pdf',
      pageSize: 'A4',
      styles: {
        background: {
          margin: [-20, 0, 0, 0],
        },
        title: {
          fontSize: 20,
          alignment: 'left',
          margin: [0, 0, 0, 15],
          color: '#14142B',
          textBaseline: 'bottom',
        },
        date: {
          fontSize: 12,
          alignment: 'right',
          margin: [0, 0, 0, 15],
          textBaseline: 'bottom',
        },
        descriptor: {
          color: '#14142B',
          fontSize: 10,
          textBaseline: 'top',
          margin: [0, 10, 0, 1],
        },
        value: {
          color: '#14142B',
          fontSize: 12,
          bold: true,
          textBaseline: 'top',
        },
        makler: {
          margin: [0, -1, 0, 1]
        },
        tableTitle: {
          fontSize: 15,
          color: '#14142B',
          bold: true,
          alignment: 'left',
          margin: [0, 16, 0, 4]
        },
        tableHeader: {
          fillColor: '#EFF0F7',
          fontSize: 10,
        }
      },
      content: [],
    };

    this.proposalSlip.filteredOffers.forEach((offer) => {
      const offerContent = this.getContent(offer);
      docDefinition.content.push(...offerContent);
    });

    this.pdfDocGenerator = pdfMake.createPdf(docDefinition);
    this.pdfDocGenerator.getDataUrl((url) => document.getElementById('proposalSlip')?.setAttribute('src',url));
  }

  private getContent(offer: any) {

    return [
      {
        columns: [
          {
            style: 'title',
            type: 'text',
            text: this.translate.instant('proposalSlip.title') + ' Nr.' + this.formatUuid(this.proposalSlip.proposalInformation.id),
          },
          {
            style: 'date',
            type: 'text',
            text: this.translate.instant('proposalSlip.issuedAt') +
              ' ' + this.datePipe.transform(this.proposalSlip.issuedAt, 'dd.MM.YYYY'),
          },
        ],
      },
      {
        columns: [
          {
            style: 'descriptor',
            type: 'text',
            text: this.translate.instant('proposalSlip.proposalId'),
            alignment: 'left',
            width: 220
          },
          {
            style: 'descriptor',
            type: 'text',
            text: this.translate.instant('proposalSlip.agent'),
            alignment: 'left',
          },
          {
            style: 'descriptor',
            type: 'text',
            text: this.translate.instant('proposalSlip.telephoneNumber'),
            alignment: 'left',
            width: 'auto'
          },
        ],
      },
      {
        columns: [
          {
            style: 'value',
            type: 'text',
            text: this.formatUuid(this.proposalSlip.proposalInformation.id),
            alignment: 'left',
            width: 220
          },
          {
            style: 'value',
            type: 'text',
            text: this.proposalSlip.user.company.name,
            alignment: 'left',
          },
          {
            style: 'value',
            type: 'text',
            text: this.proposalSlip.user.telephoneNumber,
            alignment: 'left',
            width: 'auto'
          },
        ],
      },
      {
        columns: [
          {
            style: 'descriptor',
            type: 'text',
            text: this.translate.instant('proposalSlip.riskDateBegin'),
            alignment: 'left',
            width: 65
          },
          {
            style: 'descriptor',
            type: 'text',
            text: this.translate.instant('proposalSlip.riskDateEnd'),
            alignment: 'left'
          },
          (this.proposalSlip.partner.name != null) ? {
            style: 'descriptor',
            type: 'text',
            text: this.translate.instant('proposalSlip.entityEnsured'),
            alignment: 'right',
            width: 200
          } : null,
        ],
      },
      {
        columns: [
          {
            style: 'value',
            type: 'text',
            text: this.datePipe.transform(this.proposalSlip.proposalInformation.riskDateBegin, 'dd.MM.YY') +
              '  -  ' + this.datePipe.transform(this.proposalSlip.proposalInformation.riskDateEnd, 'dd.MM.YY'),
            alignment: 'left',
          },
          (this.proposalSlip.partner.name != null) ? {
            style: 'value',
            type: 'stack',
            stack: [
              (this.proposalSlip.partner.name ?? ''),
              this.proposalSlip.partner.street +
              ' ' + this.proposalSlip.partner.houseNumber,
              this.proposalSlip.partner.postalCode +
              ' ' + this.proposalSlip.partner.location
            ],
            alignment: 'right',
            width: 200,
          } : null,
        ]
      },
      {
        style: 'tableTitle',
        type: 'text',
        text: this.translate.instant('proposalSlip.lineOfBusiness'),
        alignment: 'left',
      },
      {
        table: {
          headerRows: 1,
          widths: ['*', 200, 60],
          body: this.getLineOfBusinessTable()
        }
      },
      {
        style: 'tableTitle',
        type: 'text',
        text: this.translate.instant('proposalSlip.deductible'),
        alignment: 'left',
      },
      {
        table: {
          headerRows: 1,
          widths: ['*', 'auto', 'auto'],
          body: this.getDeductibleTable(offer)
        }
      },
      {
        style: 'tableTitle',
        type: 'text',
        text: this.translate.instant('proposalSlip.premiums'),
        alignment: 'left',
      },
      {
        table: {
          headerRows: 1,
          widths: ['*', 'auto', 'auto'],
          body: this.getPremiumTable(offer),
        },
      },
      (this.getWordingTable(offer) != null) ? {
        style: 'tableTitle',
        type: 'text',
        text: this.translate.instant('proposalSlip.wording'),
        alignment: 'left',
      }: null,
      (this.getWordingTable(offer) != null) ? {
        table: {
          widths: ['auto', '*'],
          body: this.getWordingTable(offer)
        },
      }: null,
      (this.proposalSlip.damages.length != 0) ? {
        style: 'tableTitle',
        type: 'text',
        text: this.translate.instant('proposalSlip.previousDamages'),
        alignment: 'left',
      }: null,
      (this.proposalSlip.damages.length != 0) ? {
        table: {
          headerRows: 1,
          widths: ['*', '*', '*'],
          body: this.getPreviousDamageTable()
        },
      }: null,
      {
        style: 'tableTitle',
        type: 'text',
        text: this.translate.instant('proposalSlip.participation'),
      },
      {
        table: {
          headerRows: 1,
          widths: ['*', 'auto', 'auto'],
          body: this.getOfferTable(offer)
        },
      },
      (offer != this.proposalSlip.filteredOffers[this.proposalSlip.filteredOffers.length-1]) ? {
        text: "",
        pageBreak: 'after'
      }: null
    ];
  }


  getLineOfBusinessTable() {
    let headerRow = [this.createTableHeaderCell(this.translate.instant('proposalSlip.typ'), 'left'),
      this.createTableHeaderCell(this.translate.instant('proposalSlip.maxima'), 'right'),
      this.createTableHeaderCell(this.translate.instant('proposalSlip.currency'), 'left')];

    let tableBody = [];
    tableBody.push(headerRow);

    let rowData = [];
    const lineOfBusiness = this.translate.instant(`lineOfBusiness.clauses.${this.proposalSlip.lineOfBusiness.number}.name`);
    const sumInsured = this.number.transform(this.totalInsuredAmount, Formats.CURRENCY, this.localeService.getLocale());

    rowData.push(this.createTableCell((lineOfBusiness ?? ''), 'left'));
    rowData.push(this.createTableCell(sumInsured, 'right'));
    const isValues1914 = this.proposalSlip.lineOfBusiness?.number === '140' && this.proposalSlip.proposalInformation.insuredSumType === 'VALUES_1914';
    rowData.push(this.createTableCell(isValues1914 ? '' : this.proposalSlip.proposalInformation.currency, 'left'));


    let lastRow = [];
    lastRow.push({ text: this.translate.instant('proposalSlip.furtherDetails'),
      colSpan: 3,
      border: [false, false, false, false],
      alignment: 'right',
      fontSize: 8,
    });

    tableBody.push(rowData);
    tableBody.push(lastRow);

    return tableBody;
  }

  getDeductibleTable(offer) {
    let headerRow = [this.createTableHeaderCell(this.translate.instant('proposalSlip.typ'), 'left'),
      this.createTableHeaderCell(this.translate.instant('proposalSlip.sum'), 'right'),
      this.createTableHeaderCell(this.translate.instant('proposalSlip.currency'), 'right')];

    let tableBody = [];
    tableBody.push(headerRow);

    let rowData = [];
    rowData.push(this.createTableCell(this.translate.instant(`proposalSlip.typeOfDeductibleOptions.${offer.typeOfDeductible}`), 'left'));
    if (offer.typeOfDeductible == QuestionOptions.typeOfDeductible[0]) {
      rowData.push(this.createTableCell('', 'center'));
      rowData.push(this.createTableCell('', 'center'));
    } else {
      const deductibleAbsoluteAmount = this.number.transform(offer.deductibleAbsoluteAmount, Formats.CURRENCY,
        this.localeService.getLocale());
      rowData.push(this.createTableCell(deductibleAbsoluteAmount ?? '', 'right'));
      rowData.push(this.createTableCell(offer.deductibleCurrency ?? '', 'left'));
    }

    tableBody.push(rowData);

    return tableBody;

  }


  getPremiumTable(offer) {
    let headerRow = [this.createTableHeaderCell(this.translate.instant('proposalSlip.typ'), 'left'),
      this.createTableHeaderCell(this.translate.instant('proposalSlip.sum'), 'right'),
      this.createTableHeaderCell(this.translate.instant('proposalSlip.currency'), 'right')];

    let tableBody = [];
    tableBody.push(headerRow);

    let firstRow = [];
    firstRow.push(this.createTableCell(this.translate.instant('proposalSlip.referenceValue') + ': ' + this.translate.instant('proposalDetails.createOfferTab.referenceValues.' + offer.referenceValue) + '*', 'left'));
    firstRow.push(this.createTableCell(this.number.transform(offer.premium, Formats.CURRENCY, this.localeService.getLocale()), 'right'));
    firstRow.push(this.createTableCell(offer.currency,'left'));

    let premiumInPercent: number = 0;
    if (offer.referenceValue == QuestionOptions.referenceValues[2]) {
      premiumInPercent = (offer.premium / this.proposalSlip.proposalInformation.annualSalesAmount) * 100;
    } else {
      premiumInPercent = (offer.premium / this.proposalSlip.proposalInformation.insuredAmount) * 100;
    }
    let secondRow = [];
    secondRow.push(this.createTableCell(this.translate.instant('proposalSlip.premium'), 'left'));
    secondRow.push(this.createTableCell(this.number.transform(premiumInPercent, Formats.PERCENT, this.localeService.getLocale()) + ' %', 'right'));
    secondRow.push(this.createTableCell(' ','left'));

    let thirdRow = [];
    thirdRow.push({
        text: this.translate.instant('proposalSlip.expectedTotalPremium') + '*',
        border: [false, true, false, false],
        alignment: 'left',
        borderColor: ['#000', '#DADCEA', '#000', '#000'],
        margin: [10, 2],
        fillColor: '#FCFCFC',
        fontSize: 12,
        bold: true
      });

    thirdRow.push({
      text: this.number.transform(offer.expectedTotalPremium, Formats.CURRENCY, this.localeService.getLocale()),
      border: [false, true, false, false],
      alignment: 'right',
      borderColor: ['#000', '#DADCEA', '#000', '#000'],
      margin: [10, 2],
      fillColor: '#FCFCFC',
      fontSize: 12,
      bold: true
    });

    thirdRow.push({
      text: offer.currency,
      border: [false, true, false, false],
      alignment: 'left',
      borderColor: ['#000', '#DADCEA', '#000', '#000'],
      margin: [10, 2],
      fillColor: '#FCFCFC',
      fontSize: 12,
      bold: true
    });

    let fourthRow = [];
    fourthRow.push(this.createTableCell(this.translate.instant('proposalSlip.advancePremium') + '*', 'left'));
    fourthRow.push(this.createTableCell(this.number.transform(offer.advancePremium, Formats.CURRENCY, this.localeService.getLocale()), 'right'));
    fourthRow.push(this.createTableCell(offer.currency, 'left'));

    let fifthRow = [];
    fifthRow.push(this.createTableCell(this.translate.instant('proposalSlip.minimumPremium') + '*', 'left'));
    fifthRow.push(this.createTableCell(this.number.transform(offer.minimumPremium, Formats.CURRENCY, this.localeService.getLocale()), 'right'));
    fifthRow.push(this.createTableCell(offer.currency, 'left'));

    let sixthRow = [];
    sixthRow.push({ text: '*' + this.translate.instant('proposalSlip.additionalTaxes'),
      colSpan: 3,
      border: [false, false, false, false],
      alignment: 'right',
      fontSize: 8,
      margin: [10, 0, 10, 5],
     });

    tableBody.push(firstRow);
    tableBody.push(secondRow);
    tableBody.push(thirdRow);
    if (this.advancePremiumSum > 0) tableBody.push(fourthRow);
    if (this.minimumPremiumSum > 0) tableBody.push(fifthRow);
    tableBody.push(sixthRow);

    return tableBody;
  }

  getWordingTable(offer) {
    const standardTermAndConditions = offer?.standardTermsAndConditions?.join('\n - ');
    const individualTermsAndConditions = offer?.individualTermsAndConditions;
    const standardClauses = offer?.standardClauses?.join('\n - ');
    const individualClauses = offer?.individualClauses;

    let headerRow = [this.createTableHeaderCell(this.translate.instant('proposalSlip.wordingTitle'), 'left'),
      this.createTableHeaderCell('', 'right')];

    let tableBody = [];
    tableBody.push(headerRow);

    if(standardTermAndConditions) {
      let firstRow = [];
      firstRow.push(this.createTableCell(this.translate.instant('proposalSlip.standardTerms'), 'left'));
      firstRow.push(this.createTableCell('- ' + standardTermAndConditions, 'left'));
      tableBody.push(firstRow);
    }

    if(individualTermsAndConditions) {
      let secondRow = [];
      secondRow.push(this.createTableCell(this.translate.instant('proposalSlip.individualTerms'), 'left'));
      secondRow.push(this.createTableCell(individualTermsAndConditions, 'left'));
      tableBody.push(secondRow);
    }

    if(standardClauses) {
      let thirdRow = [];
      thirdRow.push(this.createTableCell(this.translate.instant('proposalSlip.standardClauses'), 'left'));
      thirdRow.push(this.createTableCell('- ' + standardClauses, 'left'));
      tableBody.push(thirdRow);
    }

    if(individualClauses) {
      let fourthRow = [];
      fourthRow.push(this.createTableCell(this.translate.instant('proposalSlip.individualClauses'), 'left'));
      fourthRow.push(this.createTableCell(individualClauses, 'left'));
      tableBody.push(fourthRow);
    }

    if (standardTermAndConditions || individualTermsAndConditions || standardClauses || individualClauses) return tableBody;
    else return null;
  }

  getPreviousDamageTable() {
    let headerRow = [this.createTableHeaderCell(this.translate.instant('proposalSlip.year'), 'left'),
      this.createTableHeaderCell(this.translate.instant('proposalSlip.damageType'), 'right'),
      this.createTableHeaderCell(this.translate.instant('proposalSlip.amountPaid'), 'right')];

    let tableBody = [];
    tableBody.push(headerRow);

    this.proposalSlip.damages.forEach(damage => {
      let rowData = [];
      rowData.push(this.createTableCell(damage.year.toString(), 'left'));

      rowData.push(this.createTableCell(damage.type, 'right'));
      const amountPaid = this.number.transform(damage.amountPaid, Formats.CURRENCY,
        this.localeService.getLocale());
      rowData.push(this.createTableCell(amountPaid + ' ' + damage.currency, 'right'));

      tableBody.push(rowData);
    });

    return tableBody;

  }

  getOfferTable(offer) {
    let headerRow = [this.createTableHeaderCell(this.translate.instant('proposalSlip.insurer'), 'left'),
      this.createTableHeaderCell(this.translate.instant('proposalSlip.share'), 'right'),
      this.createTableHeaderCell(this.translate.instant('proposalSlip.isLead'), 'right')];

    let tableBody = [];
    tableBody.push(headerRow);

    let rowData = [];
    rowData.push(this.createTableCell(offer.companyName, 'left'));
    const share = this.number.transform(offer.amountCovered * 100, Formats.PERCENT,
      this.localeService.getLocale());
    rowData.push(this.createTableCell(share + '%', 'right'));
    rowData.push(this.createTableCell(this.isLeadInsurer(offer.isLeading), 'right'));

    tableBody.push(rowData);

    return tableBody;
  }

  createTableCell(text: string, alignment: 'left' | 'center' | 'right'): any {
    return {
      text,
      border: [false, true, false, false],
      alignment,
      borderColor: ['#000', '#DADCEA', '#000', '#000'],
      margin: [10, 2],
      fillColor: '#FCFCFC',
      fontSize: 12
    };
  }

  createTableHeaderCell(text: string, alignment: 'left' | 'center' | 'right'): any {
    return {
      text,
      border: [false, false, false, false],
      alignment,
      margin: [10, 2],
      fillColor: '#EFF0F7',
      fontSize: 10,
    };
  }


  private loadBackgroundImage(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let imageUrl = '../../../assets/slip/watermark-siegel.png';

      const xhr = new XMLHttpRequest();
      xhr.open('GET', imageUrl, true);
      xhr.responseType = 'blob';

      xhr.onload = () => {
        const reader = new FileReader();

        reader.onload = () => {
          this.backgroundImageIcon = reader.result as string;
          resolve();
        };

        reader.readAsDataURL(xhr.response);
      };

      xhr.send();
    });

  }
  downloadPdf() {
    this.pdfDocGenerator.download('HVB Börsennote '+ this.proposalSlip.proposalInformation.id.slice(0,8) + '.pdf');
  }

  openPrivateNoteDialog() {
    let privateNote: PrivateNote = null;
    this.privateNote$.subscribe(pn => privateNote = pn);

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = false;
    dialogConfig.data = {
      title: this.translate.instant('privateNotesDialog.title'),
      placeholder: this.translate.instant('privateNotesDialog.placeholder'),
      optionSave: this.translate.instant('privateNotesDialog.save'),
      optionCancel: this.translate.instant('privateNotesDialog.cancel'),
      location: PrivateNoteLocation.SLIP,
      note: privateNote?.note,
      id: privateNote?.id,
      proposalId: this.proposalSlip.proposalInformation.id
    };
    this.dialog.open(PrivateNotesDialogComponent, dialogConfig);
  }

}
